import type { PosOrderDetail } from './api';
import type { ReceiptData } from '../components/receipt';

/**
 * The ONE mapping from a fetched POS order to the printable tape.
 *
 * Shared by the releasing queue and the Transaction History reprint so the two
 * can never drift. That drift is not hypothetical: the tape once printed
 * totals with no line items because a call site built its own object and
 * omitted `items` (fixed in 9ecef44). A reprint that disagrees with the
 * original tape is worse than no reprint at all — the whole point is that a
 * customer's copy and the shop's copy match.
 *
 * The checkout flow in pos.tsx deliberately does NOT use this: it renders
 * straight from the checkout response, which carries change-due and tendered
 * figures that only exist at the moment of sale.
 */
export function orderToReceipt(o: PosOrderDetail): ReceiptData {
  const payment = o.payments[0] ?? null;
  return {
    orderCode: o.code,
    status: o.status,
    items: o.items,
    subtotal: o.subtotal,
    discountTotal: o.discountTotal,
    total: o.total,
    payment: payment
      ? {
          method: payment.method,
          methodLabel: payment.methodLabel,
          amountTendered: payment.amountTendered,
          changeDue: payment.changeDue,
        }
      : null,
    kitCodes: o.kitCodes.map((c) => ({ code: c.code, pin: c.pin })),
    header: o.branch?.receiptHeader ?? null,
    customFeeLabel: o.customFeeLabel,
    customFeeAmount: o.customFeeAmount,
    customFees: o.customFees,
    memberName: o.buyerName,
    cashierName: o.cashierName,
    branchName: o.branch?.name ?? null,
    createdAt: o.createdAt,
    remarks: o.notes,
    fields: o.branch?.receiptFields,
  };
}
